/**
 * Traduction des refus d'écriture renvoyés par les RPC de vote.
 *
 * `submit_poll_response` et `upsert_poll_response` lèvent des erreurs nommées
 * (`invite_required`, `auth_required`, `poll_full`…). Sans mapping, c'est ce
 * nom technique anglais qui s'affiche au votant.
 *
 * Le gate d'invitation évite la plupart de ces cas en amont, mais pas tous :
 * une invitation peut être révoquée entre le préflight et l'envoi, et le
 * serveur reste de toute façon le seul arbitre. Les clés existent déjà dans
 * `errors.json` (fr/en/es) — aucune n'est créée ici.
 */

/** Nom levé par le SQL → clé i18n. Les noms sont testés tels quels : aucun
 *  n'est préfixe d'un autre, l'ordre de parcours n'a donc pas d'importance. */
const CODE_TO_KEY: Record<string, string> = {
  invite_required: "errors:vote_invite_required",
  invite_invalid: "errors:vote_invite_invalid",
  invite_revoked: "errors:vote_invite_revoked",
  invite_expired: "errors:vote_invite_expired",
  invite_exhausted: "errors:vote_invite_exhausted",
  invite_bound_elsewhere: "errors:vote_invite_bound_elsewhere",
  invite_email_mismatch: "errors:vote_invite_email_mismatch",
  auth_required: "errors:vote_auth_required",
  poll_full: "errors:vote_poll_full",
  edit_forbidden: "errors:vote_edit_forbidden",
  maybe_not_allowed: "errors:vote_maybe_not_allowed",
};

/**
 * Renvoie la clé i18n correspondant à l'erreur, ou `null` si ce n'est pas un
 * refus d'enforcement connu (auquel cas l'appelant garde son message habituel).
 */
export function voteErrorKey(error: unknown): string | null {
  const raw =
    error instanceof Error
      ? error.message
      : typeof (error as { message?: unknown } | null)?.message === "string"
        ? ((error as { message: string }).message)
        : "";
  if (!raw) return null;

  for (const [code, key] of Object.entries(CODE_TO_KEY)) {
    if (raw.includes(code)) return key;
  }

  // Message historique de l'edge function, antérieur aux codes nommés.
  if (raw.includes("Authentication required")) {
    return "errors:vote_auth_required";
  }
  return null;
}

/**
 * Message traduit pour l'erreur, ou `fallback` si le refus n'est pas reconnu.
 * `t` est la fonction de `useTranslation` — passée en paramètre pour que ce
 * module reste testable sans monter i18n.
 */
export function voteErrorMessage(
  error: unknown,
  t: (key: string) => string,
  fallback: string,
): string {
  const key = voteErrorKey(error);
  return key ? t(key) : fallback;
}
